import { z } from 'zod';
import { count, isoDate, isoTimestamp, ratio } from './primitives.js';
/*
 * GET /leads — the lead → booking loop: does a captured lead actually become
 * a booked prøvetime, and who is still waiting for a call?
 *
 * This is the number the quiz-vs-landing-page decision rests on. Leads live in
 * lead_submissions (every /api/lead attempt, incl. the ones Momence rejected);
 * bookings live in our own bookings table. Nothing joined them until now.
 *
 * One row per PERSON, not per submission: repeat submissions from the same
 * phone/email collapse into one entry, so the worklist never shows the same
 * human twice and the conversion rate can't be inflated by duplicates.
 *
 * Contains names + phone numbers by design — it is an operational call list,
 * served only behind the Google-identity dashboard auth (or the admin Bearer
 * mirror). Never expose it publicly.
 */
const ok = z.literal(true);
/** How a lead was tied to a booking (phone is the strong one). */
export const leadMatchMethodSchema = z.enum(['phone', 'email', 'name']);
/** Where the lead came from. */
// 'meta' = Meta Lead Ads, which land in meta_leads and go straight to Momence.
// They were invisible here until the tab started reading that table too.
export const leadSourceSchema = z.enum(['quiz', 'meta', 'annet']);
/** The booking a lead turned into, when it did. */
export const leadBookingSchema = z.object({
    ref: z.string(),
    bookedAt: isoTimestamp,
    slotStartsAt: isoTimestamp,
    /** Booking row status: 'new' | 'synced' | 'error' | 'cancelled'. */
    status: z.string(),
    cancelled: z.boolean(),
    matchedBy: leadMatchMethodSchema,
    /** Hours from the (first) lead to the booking. */
    hoursToBook: z.number(),
});
/** One person who left their details, with the follow-up state. */
/**
 * What Momence knows about the lead, across EVERY channel — our widget, the
 * Momence widget, and meetings the host booked over the phone. `booking` below
 * only ever knew our own widget, which is why 98 already-booked leads sat on
 * the call list looking like nobody had answered.
 */
export const leadMomenceOutcomeSchema = z.object({
    slotAt: isoTimestamp,
    cancelled: z.boolean(),
    /** Checked in at the studio — the appointment actually happened. */
    showed: z.boolean(),
    member: z.boolean(),
    memberSince: isoTimestamp.nullable(),
    /** 'name' is a GUESS — two strangers can share one. The table marks it. */
    matchedBy: leadMatchMethodSchema,
});
export const leadPersonSchema = z.object({
    key: z.string(), // stable identity key (hashed-free: the normalized phone/email/name)
    name: z.string(),
    phone: z.string().nullable(),
    email: z.string().nullable(),
    source: leadSourceSchema,
    /** Staff/test row (INTERNAL_PHONES or an obvious test name) — kept out of every headline number. */
    internal: z.boolean(),
    firstLeadAt: isoTimestamp,
    lastLeadAt: isoTimestamp,
    /** How many times this person submitted (duplicates collapsed into this row). */
    submissions: count,
    /** True when at least one submission reached Momence. */
    /**
     * What our forward to Momence PROVED. Deliberately NOT a boolean called
     * "inMomence": Momence has no lead-read API, so we can never establish that
     * someone is absent — only that our own request succeeded, timed out, or was
     * rejected.
     */
    momenceForward: z.enum(['sent', 'uncertain', 'rejected', 'not_sent']),
    momenceLeadId: z.string().nullable(),
    /** Status of the newest submission ('forwarded' | 'failed' | 'invalid' | …). */
    status: z.string(),
    /** They accepted a call — «Ja, gjerne» on step 8 or the result-page button. */
    callbackRequested: z.boolean(),
    /** Momence outcome, any channel. Null when we find no appointment for them. */
    momence: leadMomenceOutcomeSchema.nullable(),
    /**
     * They ticked «Nei takk» on step 8. A stated preference, not a missing
     * answer: these people must never appear on a call list.
     */
    doNotCall: z.boolean(),
    /** The quiz answers as stored on the lead (newline-per-answer), if any. */
    answers: z.string().nullable(),
    /** Age of the lead in hours (from the first submission). */
    hoursSinceLead: z.number(),
    /** The conversion: a booking made AT OR AFTER the lead. Null until they book. */
    booking: leadBookingSchema.nullable(),
    /**
     * A booking they already had BEFORE this lead (90-day lookback). Shown so
     * nobody is chased twice — never counted as a conversion.
     */
    priorBooking: z
        .object({ ref: z.string(), bookedAt: isoTimestamp, cancelled: z.boolean() })
        .nullable(),
});
/** Per-source rollup (quiz vs everything else). */
export const leadBySourceSchema = z.object({
    source: leadSourceSchema,
    people: count,
    booked: count,
    bookedPct: ratio,
    /** Leads older than 48h — the fair-chance cohort. */
    maturePeople: count,
    bookedPctMature: ratio,
});
export const leadsResponse = z.object({
    ok,
    from: isoDate,
    to: isoDate,
    /** A read cap was hit — the numbers cover a truncated window. */
    truncated: z.boolean(),
    totals: z.object({
        /** Distinct REAL people who left details in the range (internal excluded). */
        people: count,
        /** …of whom this many booked afterwards (cancelled bookings not counted). */
        booked: count,
        /** booked ÷ people — THE quiz-vs-LP decision number. */
        bookedPct: ratio,
        /** Not booked yet — the call list. */
        waiting: count,
        /** Of `waiting`: leads younger than 48h, still inside their fair-chance window. */
        tooFresh: count,
        /** Leads older than 48h — the cohort behind bookedPctMature. */
        maturePeople: count,
        /** booked-among-mature ÷ maturePeople — same cohort both sides. */
        bookedPctMature: ratio,
        /** Waiting people who explicitly asked to be called. */
        callbacksPending: count,
        /** Waiting people who asked NOT to be called — kept off the call list. */
        doNotCall: count,
        /** Momence REJECTED these ('failed') — they exist only with us until fixed. */
        /**
         * The targeted call-list check: when it last ran and whether it worked.
         * 'never' and 'error' must read differently from 'ok' — otherwise the page
         * cannot tell "checked and found nothing" from "has not checked in hours".
         */
        liveCheck: z.object({
            /** 'partial' = the run finished but some Momence lookups failed. */
            status: z.enum(['ok', 'partial', 'error', 'never']),
            /** When the last COMPLETED run started. A run in flight is not a failure. */
            at: z.string().nullable(),
            /**
             * When the check last actually SUCCEEDED. Separate from `at` because a
             * failing run's start time labelled "last OK" is exactly the unfounded
             * claim this stamp exists to prevent — it made an hours-dead check read
             * as five minutes old.
             */
            okAt: z.string().nullable(),
        }),
        momenceUncertain: count,
        momenceMissing: count,
        /** Conversions that were later cancelled (excluded from `booked`). */
        cancelledConversions: count,
        /** Staff/test rows filtered out of every number above. */
        internal: count,
        /** Median hours from lead to booking (converted people only). */
        medianHoursToBook: z.number().nullable(),
    }),
    bySource: z.array(leadBySourceSchema),
    /** Everyone in the range: waiting first (most urgent), then booked. */
    people: z.array(leadPersonSchema),
});
//# sourceMappingURL=leads.js.map